import createIntlWithLocale from 'src/internal/createIntlWithLocale';
import selectUnit from 'src/internal/date-formatter/selectUnit';
import {handleReactIntlError} from 'src/internal/errors/handleReactIntlError/handleReactIntlError';
import mergeOptions from 'src/internal/mergeOptions';

import type {IAnalytics, IReports} from '@sail/observability';
import type {InputDate} from 'src/internal/date-formatter/types';
import type {
  FormatRelativeTimeOptionsDefaults,
  FormatRelativeTimeOptionsWithDefaults,
} from 'src/public/formatRelativeTime/types';
import type {IntlConfig} from 'src/types';

/**
 * @see https://sail.stripe.me/apis/intl/useIntl/#format-relative-time
 */
export default function formatRelativeTime(
  /** The intl config object containing the locale data. When using this API through the `useIntl` hook, this config object is automatically injected. */
  config: IntlConfig,
  reports: IReports,
  analytics: IAnalytics,
  /** The start date. */
  from: InputDate,
): string {
  const defaults: FormatRelativeTimeOptionsDefaults = {
    locale: config.locale,
  };

  const mergedOptions: FormatRelativeTimeOptionsWithDefaults = mergeOptions(
    config,
    defaults,
    reports,
  );

  const intl = createIntlWithLocale(
    mergedOptions.locale,
    config,
    reports,
    analytics,
  );
  const {value, unit, numeric} = selectUnit(from, Date.now());

  try {
    return intl.formatRelativeTime(value, unit, {
      numeric,
      style: 'long',
    });
  } catch (error) {
    handleReactIntlError(reports, analytics, config.locale, error);

    return '';
  }
}

type FormatRelativeTime = typeof formatRelativeTime;

export type FormatRelativeTimeDeclarative = (
  from: Parameters<FormatRelativeTime>[3],
) => ReturnType<FormatRelativeTime>;
